import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ContactPhotoUpload = ({ 
  currentPhoto = null, 
  onPhotoChange = () => {},
  onPhotoRemove = () => {},
  className = '' 
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [tempPhoto, setTempPhoto] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) { // 5MB limit
        alert('File size must be less than 5MB');
        return;
      }
      
      if (!file?.type?.startsWith('image/')) {
        alert('Please select an image file');
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setTempPhoto(e?.target?.result);
        setShowCropModal(true);
      };
      reader?.readAsDataURL(file);
    }
  };

  const handleCameraClick = () => {
    fileInputRef?.current?.click();
  };

  const handleCropConfirm = () => {
    setIsUploading(true);
    // Simulate upload delay
    setTimeout(() => {
      onPhotoChange(tempPhoto);
      setShowCropModal(false);
      setTempPhoto(null);
      setIsUploading(false);
    }, 1000);
  };

  const handleCropCancel = () => {
    setShowCropModal(false);
    setTempPhoto(null);
  };

  const handleRemovePhoto = () => {
    onPhotoRemove();
  };

  return (
    <>
      <div className={`flex flex-col items-center space-y-4 ${className}`}>
        {/* Photo Display Area */}
        <div className="relative">
          <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full border-4 border-border bg-muted overflow-hidden flex items-center justify-center">
            {currentPhoto ? (
              <Image
                src={currentPhoto}
                alt="Contact photo"
                className="w-full h-full object-cover"
              />
            ) : (
              <Icon name="User" size={48} className="text-text-secondary" />
            )}
          </div>

          {/* Upload/Edit Button */}
          <button
            onClick={handleCameraClick}
            className="absolute bottom-2 right-2 w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center shadow-modal hover-scale transition-micro"
          >
            <Icon name="Camera" size={20} />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCameraClick}
            iconName="Upload"
            iconPosition="left"
          >
            Upload Photo
          </Button>

          {currentPhoto && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemovePhoto}
              iconName="Trash2"
              iconPosition="left"
              className="text-error hover:text-error"
            >
              Remove
            </Button>
          )}
        </div>

        {/* Hidden File Input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
        />
      </div>

      {/* Crop Modal */}
      {showCropModal && (
        <div className="fixed inset-0 z-500 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-surface rounded-lg shadow-modal max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-text-primary">
                  Crop Photo
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={handleCropCancel}
                >
                  <Icon name="X" size={20} />
                </Button>
              </div>

              {/* Crop Preview */}
              <div className="mb-6">
                <div className="w-full h-64 bg-muted rounded-lg overflow-hidden flex items-center justify-center">
                  {tempPhoto && (
                    <Image
                      src={tempPhoto}
                      alt="Photo preview"
                      className="max-w-full max-h-full object-contain"
                    />
                  )}
                </div>
                <p className="text-sm text-text-secondary mt-2 text-center">
                  Photo will be automatically cropped to fit circular frame
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex space-x-3">
                <Button
                  variant="outline"
                  onClick={handleCropCancel}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  onClick={handleCropConfirm}
                  loading={isUploading}
                  className="flex-1"
                >
                  {isUploading ? 'Uploading...' : 'Confirm'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ContactPhotoUpload;